import { useQuery } from '@tanstack/react-query';
import { listClubs, listMembers } from '../api/clubs';
import { canLogForOthers } from '../lib/permissions';
import { useAuth } from '../contexts/AuthContext';
import type { ClubRole } from '../types/api';

export interface OnBehalfTargetMember {
  userId: string;
  displayName: string;
  role: ClubRole;
}

export interface OnBehalfTargetClub {
  clubId: number;
  clubName: string;
  members: OnBehalfTargetMember[];
}

export function useOnBehalfTargets() {
  const { user } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin ?? false;
  const callerId = user?.id;

  return useQuery<OnBehalfTargetClub[]>({
    queryKey: ['on-behalf-targets', callerId, isSuperAdmin],
    enabled: !!callerId,
    queryFn: async () => {
      const clubs = await listClubs();
      const eligibleClubs = clubs.filter((c) => canLogForOthers(c.role, isSuperAdmin));
      const rosters = await Promise.all(
        eligibleClubs.map(async (club) => {
          const members = await listMembers(club.id);
          return {
            clubId: club.id,
            clubName: club.name,
            members: members
              .filter((m) => m.userId !== callerId && m.role !== 'Guest')
              .map((m) => ({
                userId: m.userId,
                displayName: m.displayName,
                role: m.role,
              })),
          };
        }),
      );
      return rosters.filter((r) => r.members.length > 0);
    },
  });
}
